<template>
  <DebugInspector :data="room" label="Room">
    <div class="room-card">
      <!-- Image carousel -->
      <div class="room-card__carousel">
        <img
          v-if="room.images && room.images.length"
          class="room-card__image"
          :src="room.images[currentImageIndex]"
          :alt="room.roomName"
        />
        <div
          v-else
          class="room-card__image room-card__image--placeholder is-flex is-justify-content-center is-align-items-center"
        >
          {{ $t("hasNoImages") }}
        </div>

        <template v-if="room.images && room.images.length > 1">
          <button
            class="room-card__arrow room-card__arrow--left"
            type="button"
            @click="prevImage"
            :disabled="currentImageIndex <= 0"
            aria-label="Previous photo"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <button
            class="room-card__arrow room-card__arrow--right"
            type="button"
            @click="nextImage"
            :disabled="currentImageIndex >= room.images.length - 1"
            aria-label="Next photo"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
          <span class="room-card__counter">
            {{ currentImageIndex + 1 }} / {{ room.images.length }}
          </span>
        </template>
      </div>

      <!-- Content -->
      <div class="room-card__body">
        <span
          class="u-display--block is-size-6"
          v-if="room.hasPriceAgreementLabel"
        >
          <span
            class="u-text-color-tertiary u-display--flex u-weight--bold is-align-items-center"
          >
            <img
              src="@/assets/images/icons/price-tag.svg"
              class="mr-1"
              width="20"
            />
            {{ $t("recommended.hotel") }}
          </span>
        </span>
        <h5 class="title is-6 mt-2 mb-1">{{ room.roomName }}</h5>
        <div v-if="roomAmenities.length" class="c-amenity-badges mt-3 mb-3">
          <div
            v-for="amenity in roomAmenities"
            :key="amenity.type"
            class="c-amenity-badges__item"
          >
            <i
              :class="[amenity.icon, 'c-amenity-badges__icon']"
              aria-hidden="true"
            ></i>
            <span>{{ $t(amenity.labelKey) }}</span>
          </div>
        </div>
        <div v-if="descriptionSummary" v-safe-html="descriptionSummary"></div>
        <a
          v-if="descriptionDetail"
          href="#"
          class="u-text-decoration--underline mt-2 mb-5"
          @click.prevent="modalOpen = true"
        >
          {{ $t("showRoomDetails") }}
        </a>

        <div class="room-card__footer">
          <div
            v-if="room.offers && room.offers.length && selectedOffer"
            class="details__price p-0"
          >
            <div class="room-card__options mt-2 mb-3">
              <label
                v-if="hasBreakfastOption"
                class="checkbox--filter mb-0 is-flex is-align-items-center"
              >
                <input
                  type="checkbox"
                  v-model="wantsBreakfast"
                  class="mr-2"
                  :id="`wantsBreakfast-${room.roomId}`"
                />
                <label
                  class="checkbox--filter--label mb-0 is-flex is-align-items-center is-justify-content-space-between u-width--100 room-card__options"
                  :for="`wantsBreakfast-${room.roomId}`"
                >
                  <div class="room-card__icon-row">
                    <img src="@/assets/images/icons/breakfast.svg" width="16" />
                    <span class="u-weight--normal">
                      {{ $t("iWantBreakfast") }}
                    </span>
                  </div>
                  <span class="u-white-space--nowrap">
                    +{{ toPriceLabel(breakfastSurcharge) }}
                  </span>
                </label>
              </label>
              <label
                v-if="hasBreakfastSpecialRequestOption"
                class="checkbox--filter mb-0 is-flex is-align-items-center"
              >
                <input
                  type="checkbox"
                  v-model="wantsBreakfastSpecialRequest"
                  class="mr-2"
                  :id="`wantsBreakfastSpecialRequest-${room.roomId}`"
                />
                <label
                  class="checkbox--filter--label mb-0 is-flex is-align-items-center is-justify-content-space-between u-width--100 room-card__options"
                  :for="`wantsBreakfastSpecialRequest-${room.roomId}`"
                >
                  <div class="room-card__icon-row">
                    <img src="@/assets/images/icons/breakfast.svg" width="16" />
                    <span class="u-weight--normal">
                      {{ $t("breakfastOnRequest") }}
                    </span>
                  </div>
                </label>
              </label>
              <div
                class="mt-2 c-amenity-badges__item u-width--100"
                v-if="!hasBreakfastOption && !hasBreakfastSpecialRequestOption"
                :class="{
                  'c-amenity-badges__item--inactive': !isBreakfastIncluded
                }"
              >
                <img src="@/assets/images/icons/breakfast.svg" width="16" />
                <span class="u-weight--normal">
                  {{
                    $t(
                      isBreakfastIncluded
                        ? "breakfastAvailable"
                        : "breakfastNotAvailable"
                    )
                  }}
                </span>
              </div>
              <label
                v-if="hasRefundableOption"
                class="checkbox--filter mb-2 is-flex is-align-items-center"
              >
                <input
                  type="checkbox"
                  v-model="wantsRefundable"
                  class="mr-2"
                  :id="`wantsRefundable-${room.roomId}`"
                />
                <label
                  class="checkbox--filter--label mb-0 is-flex is-align-items-center is-justify-content-space-between u-width--100 room-card__options mt-2"
                  :for="`wantsRefundable-${room.roomId}`"
                >
                  <div class="room-card__icon-row">
                    <img src="@/assets/images/icons/refund.svg" width="16" />
                    <span class="u-weight--normal">
                      {{ $t("iWantRefundablePrefix") }}
                      <span
                        v-if="!room.isOpenGdsMatch && !room.hasAgreedPrice"
                        class="js-tooltip"
                        style="text-decoration: underline dashed; cursor: help"
                        data-tooltip-position="top"
                        :data-tooltip-content="
                          cancellationPolicyText || $t('refundableInfo')
                        "
                        >{{ $t("iWantRefundableKeyword") }}</span
                      >
                      <span v-else>{{ $t("iWantRefundableKeyword") }}</span>
                    </span>
                  </div>
                  <span class="u-white-space--nowrap">
                    +{{ toPriceLabel(refundableSurcharge) }}
                  </span>
                </label>
              </label>
              <div
                class="mt-2 c-amenity-badges__item u-width--100"
                v-if="!hasRefundableOption"
                :class="{
                  'c-amenity-badges__item--inactive': !isRefundable
                }"
              >
                <img src="@/assets/images/icons/refund.svg" width="16" />
                <span class="u-weight--normal">
                  {{
                    $t(
                      isRefundable
                        ? "offerIsRefundablePrefix"
                        : "offerIsNotRefundablePrefix"
                    )
                  }}
                  <span
                    v-if="!room.isOpenGdsMatch && !room.hasAgreedPrice"
                    class="js-tooltip"
                    style="text-decoration: underline dashed; cursor: help"
                    data-tooltip-position="top"
                    :data-tooltip-content="
                      isRefundable
                        ? cancellationPolicyText || $t('refundableInfo')
                        : $t('nonRefundableWarning')
                    "
                    >{{ $t("refundableKeyword") }}</span
                  >
                  <span v-else>{{ $t("refundableKeyword") }}</span>
                </span>
              </div>
            </div>

            <div
              class="mb-3 is-flex is-justify-content-center is-flex-direction-column has-text-right"
            >
              <span
                class="u-font-size--normal u-weight--bold is-flex is-align-items-center is-justify-content-end"
              >
                {{ toPriceLabel(selectedOffer.nightlyTotal) }}
                <i
                  v-if="hasExceedingBudget && canBookOnBehalfOfOthers"
                  class="fa-solid fa-triangle-exclamation ml-1 js-tooltip u-text-color-orange"
                  data-tooltip-position="top"
                  :data-tooltip-content="$t('warningOverBudget', { budget })"
                ></i>
                <sub v-if="parseInt(nights) > 1">
                  &nbsp;/ {{ $t("perNight") }}</sub
                >
              </span>
              <sub v-if="parseInt(nights) > 1">
                {{ $t("total") }}
                {{ toPriceLabel(selectedOffer.stayTotalInclusive) }}
              </sub>
              <sub
                class="is-flex is-align-items-center is-justify-content-end"
                v-if="selectedOffer.nightlyTaxesAndFees"
              >
                {{ $t("includingTaxes") }}
                <div
                  class="c-icon-info c-icon-info--border js-tooltip is-inline-block c-icon-info--small mb-0"
                  data-tooltip-position="top"
                  :data-tooltip-content="
                    $t('taxesTotal', {
                      price: toPriceLabel(selectedOffer.nightlyTaxesAndFees)
                    })
                  "
                ></div
              ></sub>
            </div>

            <p
              v-if="!isBookable"
              class="has-text-black is-size-7 has-background-warning p-3"
            >
              {{ $t("roomNotBookableByUser") }}
            </p>
            <p
              v-else-if="hasExceedingBudget && !canBookOnBehalfOfOthers"
              class="has-text-black is-size-7 has-background-warning p-3"
              :class="canEditExpenses && hasExceedingBudget ? 'mb-2' : ''"
            >
              {{ $t("warningOverBudget", { budget }) }}
            </p>

            <div class="main-searchbar__submit" v-else>
              <DebugInspector :data="room.offers" label="Offers">
                <button
                  type="button"
                  class="c-button c-button--primary"
                  @click="
                    $emit('book', room, selectedOffer, {
                      wantsBreakfast,
                      wantsRefundable,
                      wantsBreakfastSpecialRequest:
                        wantsBreakfastSpecialRequest &&
                        hasBreakfastSpecialRequestOption
                    })
                  "
                >
                  {{ $t(isDirectBooking ? "bookNow" : "requestReservation") }}
                </button>
              </DebugInspector>
            </div>
          </div>
          <p class="is-size-7" v-else>
            {{ $t("noPriceFoundRequestAnyway") }}
          </p>
        </div>
      </div>

      <BaseModal
        :show="modalOpen"
        :title="room.roomName"
        @close="modalOpen = false"
      >
        <div v-safe-html="descriptionDetailFormatted"></div>
      </BaseModal>
    </div>
  </DebugInspector>
</template>

<script>
import HotelHelper from "@/helpers/hotel.helper.js";
import BaseModal from "@/components/shared/BaseModal.vue";
import CommonHelper from "@/helpers/common.helper.js";
import DebugInspector from "@/components/shared/DebugInspector.vue";
import { isExpediaRapidOffer } from "@/helpers/offer.helper.js";

export default {
  name: "RoomCard",
  components: { BaseModal, DebugInspector },
  emits: ["book"],
  props: {
    room: {
      type: Object,
      required: true
    },
    hotel: {
      type: Object,
      default: null
    },
    nights: {
      type: String,
      default: null
    },
    isBookable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentImageIndex: 0,
      modalOpen: false,
      wantsBreakfast: false,
      wantsRefundable: false,
      wantsBreakfastSpecialRequest: false,
      budget: null,
      companyInfo: null
    };
  },
  async beforeCreate() {
    const companyInfo = await CommonHelper.getCompanyInfo();
    this.companyInfo = companyInfo;
    this.budget = companyInfo.budget;
  },
  created() {
    // The backend orders room.offers so the displayed/default offer is first
    // (OfferDisplayService.PromoteSelectedOffer). Default the breakfast and
    // refundable toggles from that offer so the detail headline matches the
    // comparison card. Falls back to the cheapest offer when the list is empty.
    const preferredOffer = this.room.offers?.[0] || this.baseOffer;
    if (preferredOffer) {
      this.wantsBreakfast = preferredOffer.breakfastIncluded;
      this.wantsRefundable = preferredOffer.refundable;
    }

    // Auto-enable breakfast-on-request when that option is available.
    this.wantsBreakfastSpecialRequest = this.hasBreakfastSpecialRequestOption;
  },
  computed: {
    canEditExpenses() {
      return (
        HotelHelper.canBookOnBehalfOfOthers(this.companyInfo) &&
        HotelHelper.canEditExpenses(this.companyInfo)
      );
    },
    canBookOnBehalfOfOthers() {
      return HotelHelper.canBookOnBehalfOfOthers(this.companyInfo);
    },
    baseOffer() {
      if (!this.room.offers?.length) return null;
      return this.room.offers.reduce((cheapest, o) =>
        o.nightlyTotal < cheapest.nightlyTotal ? o : cheapest
      );
    },
    hasBreakfastOption() {
      if (!this.room.offers || !this.selectedOffer) return false;
      // If adding breakfast is a discount, it's locked as included and
      // cannot be toggled off.
      if (this.breakfastSurcharge < 0) return false;
      // Can breakfast be toggled given the currently selected offer's
      // refundable state? Only if both breakfast variants exist for it.
      const refundable = this.selectedOffer.refundable;
      return (
        this.room.offers.some(
          (o) => o.breakfastIncluded && o.refundable === refundable
        ) &&
        this.room.offers.some(
          (o) => !o.breakfastIncluded && o.refundable === refundable
        )
      );
    },
    hasBreakfastSpecialRequestOption() {
      // Some chains don't offer breakfast through Expedia (to avoid paying
      // commission) but do serve it at the hotel. When the chain is flagged
      // as isBreakfastSpecialRequest, offer a breakfast checkbox on rooms
      // whose Expedia offer has no breakfast available at all. The breakfast
      // is requested manually and carries no price on the offer.
      if (!this.hotel?.chain?.isBreakfastSpecialRequest) return false;
      if (!this.selectedOffer) return false;
      // Only when breakfast can't already be toggled or isn't included.
      if (this.hasBreakfastOption || this.isBreakfastIncluded) return false;
      // Only for offers coming through Expedia.
      return isExpediaRapidOffer(this.selectedOffer);
    },
    hasRefundableOption() {
      if (!this.room.offers || !this.selectedOffer) return false;
      // If adding refundability is a discount, it's locked as included and
      // cannot be toggled off.
      if (this.refundableSurcharge < 0) return false;
      // Can refundable be toggled given the currently selected offer's
      // breakfast state? Only if both refundable variants exist for it.
      const breakfastIncluded = this.selectedOffer.breakfastIncluded;
      return (
        this.room.offers.some(
          (o) => o.refundable && o.breakfastIncluded === breakfastIncluded
        ) &&
        this.room.offers.some(
          (o) => !o.refundable && o.breakfastIncluded === breakfastIncluded
        )
      );
    },
    isRefundable() {
      if (!this.selectedOffer) return false;
      return this.selectedOffer.refundable;
    },
    cancellationPolicyText() {
      // Build cancellation policy text from the offer's cancellation fields
      const offer = this.selectedOffer?.refundable
        ? this.selectedOffer
        : this.room.offers?.find((o) => o.refundable);
      if (!offer) return null;
      return this.buildCancellationText(offer);
    },
    isBreakfastIncluded() {
      if (!this.selectedOffer) return false;
      return this.selectedOffer.breakfastIncluded;
    },
    breakfastSurcharge() {
      if (!this.room.offers || !this.selectedOffer) return 0;
      const refundable = this.selectedOffer.refundable;
      const withBreakfast = this.getCheapestOfferForSelection(true, refundable);
      const without = this.getCheapestOfferForSelection(false, refundable);
      if (withBreakfast && without) {
        return withBreakfast.nightlyTotal - without.nightlyTotal;
      }
      return 0;
    },
    refundableSurcharge() {
      if (!this.room.offers || !this.selectedOffer) return 0;
      const breakfastIncluded = this.selectedOffer.breakfastIncluded;
      const refundable = this.getCheapestOfferForSelection(
        breakfastIncluded,
        true
      );
      const nonRefundable = this.getCheapestOfferForSelection(
        breakfastIncluded,
        false
      );
      if (refundable && nonRefundable) {
        return refundable.nightlyTotal - nonRefundable.nightlyTotal;
      }
      return 0;
    },
    selectedOffer() {
      if (!this.room.offers?.length) return null;
      // Honor the user's explicit breakfast/refundable selections. Toggle
      // availability prevents reaching combinations that don't exist, so a
      // match is always found after any user interaction.
      const match = this.getCheapestOfferForSelection(
        this.wantsBreakfast,
        this.wantsRefundable
      );
      return match || this.baseOffer;
    },
    isDirectBooking() {
      // CTA is computed at the hotel level (HotelDetail.availableRooms) and
      // passed down via room.requestReservation. true → "reservering
      // aanvragen", false → "nu boeken". See offer.helper.shouldRequestReservation.
      return !this.room.requestReservation;
    },
    hasExceedingBudget() {
      return (
        this.budget &&
        this.selectedOffer &&
        this.selectedOffer.nightlyTotal > this.budget
      );
    },
    roomAmenities() {
      return CommonHelper.getRoomAmenities(this.room);
    },
    descriptionSummary() {
      if (!this.room.description) return null;
      const brIndex = this.room.description.indexOf("<br");
      if (brIndex === -1) return null;
      const beforeBr = this.room.description.slice(0, brIndex);
      // Strip the first <p><strong>...</strong></p> block (room name)
      const withoutTitle = beforeBr
        .replace(/^<p><strong>.*?<\/strong><\/p>/i, "")
        .trim();
      return withoutTitle || null;
    },
    descriptionDetail() {
      if (!this.room.description) return null;
      // Strip the title block
      const withoutTitle = this.room.description
        .replace(/^<p><strong>.*?<\/strong><\/p>/i, "")
        .trim();
      const brIndex = withoutTitle.indexOf("<br");
      if (brIndex !== -1) {
        // Has <br> — detail is everything after it
        const afterBr = withoutTitle
          .slice(brIndex)
          .replace(/^<br\s*\/?>/, "")
          .trim();
        return afterBr || null;
      }
      // No <br> — entire content (minus title) is the detail
      return withoutTitle || null;
    },
    descriptionDetailFormatted() {
      if (!this.descriptionDetail) return null;
      const cells = [];
      this.descriptionDetail.replace(
        /<p><b>(.*?)<\/b>(.*?)<\/p>/gi,
        (match, boldText, rest) => {
          const content = rest.replace(/^\s*[-\u2013]\s*/, "").trim();
          if (!content) {
            cells.push(`<div><p><b>${boldText}</b></p></div>`);
          } else {
            const items = content
              .split(";")
              .map((s) => s.trim())
              .filter(Boolean);
            const listItems = items
              .map((item) => `<li>&nbsp;-&nbsp;${item}</li>`)
              .join("");
            cells.push(
              `<div><p><b>${boldText}</b></p><ul class="mb-2">${listItems}</ul></div>`
            );
          }
        }
      );
      return `<div class="room-detail-grid">${cells.join("")}</div>`;
    }
  },
  methods: {
    getCheapestOfferForSelection(breakfastIncluded, refundable) {
      if (!this.room?.offers?.length) return null;
      const matches = this.room.offers.filter(
        (o) =>
          o.breakfastIncluded === breakfastIncluded &&
          o.refundable === refundable
      );
      if (!matches.length) return null;
      return matches.reduce((cheapest, offer) =>
        offer.nightlyTotal < cheapest.nightlyTotal ? offer : cheapest
      );
    },
    applyDiscountLocks() {
      // When an option is cheaper with it added than without (a negative
      // surcharge), force it on so it stays included and can't be toggled
      // off. Only ever sets to true, so this converges in at most two passes
      // (one per dimension) and never loops with the watchers.
      for (let i = 0; i < 2; i++) {
        let changed = false;
        if (this.breakfastSurcharge < 0 && !this.wantsBreakfast) {
          this.wantsBreakfast = true;
          changed = true;
        }
        if (this.refundableSurcharge < 0 && !this.wantsRefundable) {
          this.wantsRefundable = true;
          changed = true;
        }
        if (!changed) break;
      }
    },
    buildCancellationText(offer) {
      if (!offer || !offer.cancellationType) return null;
      const type = offer.cancellationType;
      const date = offer.freeCancellationUntilDisplay;
      const value = offer.cancellationValue;
      if (type === "non_refundable") {
        return this.$t("nonRefundableWarning");
      }
      if (!date) return null;
      if (type === "nights") {
        return this.$t("cancellationPolicyNights", { date, value });
      }
      if (type === "percent") {
        return this.$t("cancellationPolicyPercent", { date, value });
      }
      if (type === "amount") {
        return this.$t("cancellationPolicyAmount", { date, value });
      }
      return null;
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },
    nextImage() {
      if (this.currentImageIndex < this.room.images.length - 1) {
        this.currentImageIndex++;
      }
    }
  }
};
</script>
